import { type InternalAxiosRequestConfig } from 'axios';

import COOKIE_KEYS, { type CookieKeyType } from '@/consts/cookieKeys';
import { AuthService } from '@/services/authService/client';
import cookie from '@/utils/cookies/client';
import { getBrandFromUrl } from '@/utils/http/client/getBrandFromUrl';

import { type AxiosBuilderInterceptor } from './types';

const WRITE_HTTP_VERBS = ['put', 'post', 'delete', 'patch'];

/**
 * An interceptor that checks if we already have an XSRF token, and if not
 * requests one. This is necessary for the first request from a SPA because
 * the csrf token cannot be injected into the HTML.
 *
 * @see https://laravel.com/docs/8.x/csrf
 * @see https://laravel.com/docs/8.x/sanctum#spa-authentication
 * @see https://github.com/axios/axios#request-config
 */
export const csrfHandler = (): AxiosBuilderInterceptor => ({
  type: 'request',
  handler: async (request: InternalAxiosRequestConfig) => {
    const brand = getBrandFromUrl();

    if (
      request.method &&
      WRITE_HTTP_VERBS.includes(request.method) &&
      !cookie.has((request.xsrfCookieName as CookieKeyType) ?? COOKIE_KEYS.csrf)
    ) {
      // There's no risk of an infinite loop here because we only
      // require CSRF for write requests and this is a GET request.
      try {
        await AuthService.getCsrfToken(brand);
      } catch (error) {
        throw new Error(
          'Failed to establish secure connection. Please refresh the page and try again.',
        );
      }
    }

    return request;
  },
});
